import Bugsnag from '@bugsnag/js';
import { ASTUR_DEFAULTS } from './constants';
import { assertSessionResponseCurrent, inheritSessionResponseGuard, sessionFetch, type SessionFetchOptions } from '@/session/fetch';
import type { TenantScope } from '@/session/identity';
import { emitSessionTelemetry, sanitizeTelemetryPath } from '@/session/telemetry';
import { getAccessToken } from '@/session/token';

type QueryOptions = {
  query?: Record<string, any> | string;
};

export type ApiCallRequestOptions = SessionFetchOptions & { organizationScope: TenantScope };

type ApiCallOptions = {
  method?: string;
  headers?: Headers;
  body?: any;
  query?: Record<string, any>;
  signal?: AbortSignal;
};

type ApiCallParams = {
  data?: any;
};

export interface ApiCallInstance {
  makeUrl: (url: string, queryOptions?: QueryOptions) => string;
  get: (
    path: string,
    params: Record<string, any> | undefined,
    headers: Record<string, string> | undefined,
    options: ApiCallRequestOptions
  ) => Promise<Response>;
  patch: (
    path: string,
    params: ApiCallParams,
    headers: Record<string, string> | undefined,
    options: ApiCallRequestOptions
  ) => Promise<Response> | false;
  post: (
    path: string,
    params: ApiCallParams,
    formdata: FormData | Record<string, string> | null | undefined,
    headers: Record<string, string> | undefined,
    options: ApiCallRequestOptions
  ) => Promise<Response> | false;
  put: (
    path: string,
    params: ApiCallParams,
    headers: Record<string, string> | undefined,
    options: ApiCallRequestOptions
  ) => Promise<Response> | false;
  delete: (path: string, params: any, headers: Record<string, string> | undefined, options: ApiCallRequestOptions) => Promise<Response>;
}

type LegacyApiCallInstance = {
  readonly token: string | null;
  makeUrl: ApiCallInstance['makeUrl'];
  get: (path: string, params?: Record<string, any>, headers?: Record<string, string>, options?: SessionFetchOptions) => Promise<Response>;
  patch: (path: string, params: ApiCallParams, headers?: Record<string, string>, options?: SessionFetchOptions) => Promise<Response> | false;
  post: (
    path: string,
    params: ApiCallParams,
    formdata?: FormData | Record<string, string> | null,
    headers?: Record<string, string>,
    options?: SessionFetchOptions
  ) => Promise<Response> | false;
  put: (path: string, params: ApiCallParams, headers?: Record<string, string>, options?: SessionFetchOptions) => Promise<Response> | false;
  delete: (path: string, params?: any, headers?: Record<string, string>, options?: SessionFetchOptions) => Promise<Response>;
};

export class MissingApiCallRequestScopeError extends Error {
  constructor() {
    super('Application apiCall requests require an explicit organization scope');
    this.name = 'MissingApiCallRequestScopeError';
  }
}

const createApiCall = (allowAmbientScope: boolean): LegacyApiCallInstance => {
  const createUrl = (url: string, queryOptions?: QueryOptions): string => {
    if (url.startsWith('http')) return url;
    const pathParts = url.split('/');
    let apiHost = ASTUR_DEFAULTS.config[ASTUR_DEFAULTS.defaultConfig].apiHost;

    for (const key in ASTUR_DEFAULTS.config) {
      const configKey = key as keyof typeof ASTUR_DEFAULTS.config;
      const config = ASTUR_DEFAULTS.config[configKey] as any;
      if (typeof config.matchHost !== 'undefined' && window.location.host.includes(config.matchHost)) {
        apiHost = config.apiHost;
      }
    }

    let protocol = window.location.protocol;
    if (ASTUR_DEFAULTS.defaultConfig === 'prd' || ASTUR_DEFAULTS.defaultConfig === 'dev') {
      protocol = 'https:';
    }

    let defaultUrl = `${protocol}//${apiHost}/v1/${pathParts[0]}`;
    for (const index in pathParts) {
      if (Number.parseInt(index, 10) < 1) continue;
      defaultUrl += `/${pathParts[index]}`;
    }

    if (queryOptions?.query !== undefined) {
      if (typeof queryOptions.query === 'string') {
        return `${defaultUrl}?${queryOptions.query}`;
      }
      const query = { ...queryOptions.query };
      if (typeof query.query === 'object') {
        query.query = JSON.stringify(query.query);
      }
      defaultUrl += `?${new URLSearchParams(query).toString()}`;
    }
    return defaultUrl;
  };

  const generic = async (
    url: string,
    options: ApiCallOptions = { method: 'GET' },
    formdata?: FormData | null,
    callerHeaders?: Record<string, string>,
    requestOptions?: SessionFetchOptions
  ): Promise<Response> => {
    const method = options.method || 'GET';
    const headers = new Headers();
    for (const [name, value] of Object.entries(callerHeaders ?? {})) {
      headers.set(name, value);
    }

    if (method !== 'GET') {
      if (formdata) {
        if (!headers.has('Accept')) headers.set('Accept', 'application/json');
        options.body = formdata;
      } else {
        // Default only — callers may send other JSON media types, e.g. the
        // integration save flow's application/json-patch+json.
        if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
        options.body = JSON.stringify(options.body);
      }
    } else {
      delete options.body;
    }

    const finalUrl = createUrl(url, options);
    if (!allowAmbientScope && !requestOptions?.organizationScope) {
      emitSessionTelemetry({ code: 'organization_scope_missing', transport: 'fetch', path: finalUrl });
      throw new MissingApiCallRequestScopeError();
    }
    const fetchOptions: RequestInit = {
      ...options,
      method,
      headers,
      cache: 'no-cache',
    };

    let response = await sessionFetch(finalUrl, fetchOptions, {
      ...requestOptions,
      ...(allowAmbientScope ? { allowAmbientConsoleScope: true as const } : {}),
    });
    assertSessionResponseCurrent(response);
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      Bugsnag.notify(error, (event) => {
        event.addMetadata('request_error', {
          path: sanitizeTelemetryPath(finalUrl),
          method,
          status: response.status,
          failureKind: response.status === 401 ? 'unauthenticated' : response.status === 403 ? 'denied' : 'failed',
          errorCode: 'HTTP_ERROR',
        });
      });
    }

    if (response.headers.get('content-type')?.includes('application/json')) {
      const originalResponse = response.clone();
      const data = await response.json();
      if (data.errors) {
        data.errors.error = Object.values(data.errors).join(' </br>\n');
      }
      response = inheritSessionResponseGuard(
        originalResponse,
        new Response(JSON.stringify(data), {
          status: originalResponse.status,
          statusText: originalResponse.statusText,
          headers: originalResponse.headers,
        })
      );
    }
    assertSessionResponseCurrent(response);
    return response;
  };

  return {
    get token() {
      return getAccessToken();
    },
    makeUrl: createUrl,
    get: (path, params, headers, options) => generic(path, { method: 'GET', query: params }, undefined, headers, options),
    patch: (path, params, headers, options) =>
      params.data === undefined ? false : generic(path, { method: 'PATCH', body: params.data }, undefined, headers, options),
    post: (path, params, formdata, headers, options) => {
      if (params.data === undefined) return false;
      let payload: FormData | undefined;
      let extraHeaders = headers;
      if (formdata instanceof FormData) {
        payload = formdata;
      } else if (formdata) {
        extraHeaders = { ...extraHeaders, ...formdata };
      }
      return generic(path, { method: 'POST', body: params.data }, payload, extraHeaders, options);
    },
    put: (path, params, headers, options) =>
      params.data === undefined ? false : generic(path, { method: 'PUT', body: params.data }, undefined, headers, options),
    delete: (path, params, headers, options) => generic(path, { method: 'DELETE', body: params }, undefined, headers, options),
  };
};

export const apiCall = (): ApiCallInstance => createApiCall(false) as ApiCallInstance;

// Browser-console compatibility only. Application code uses apiCall(), whose
// network methods fail closed unless an explicit organizationScope is passed.
export const legacyAmbientApiCall = (): LegacyApiCallInstance => createApiCall(true);
